import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/core';
import { fontSizes } from 'app/typography';
import { shadowLight } from 'app/colors';
import { borderRadii } from 'app/spacing';
import BaseSelect from '__components/Inputs/Select';
import {
  STEP_SLOT_HEIGHT,
  STEP_CARD_WIDTH,
  STEP_CARD_PADDING,
} from './constants';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import type { StepSlotHeight } from '../Flow/types';
import { underlineDottedStyling } from 'components/Kizen/EditableText/styles';
import { type SemanticColor } from '@kizen/kds/Colors';

const CARD_BORDER_RADIUS_OLD = borderRadii.small;
const CARD_BORDER_RADIUS = borderRadii.standard;
const CARD_BORDER_WIDTH = 2;

const getFlashAnimation = (flashBackgroundColor?: SemanticColor) => {
  if (!flashBackgroundColor) {
    return null;
  }
  return css`
    animation: ${keyframes`
    50% {
      background-color: var(--color-${flashBackgroundColor});
    }
    0%,
    100% {
      background-color: var(--color-background-white);
    }
  `} 1.5s;
  `;
};

export type StepLabel = 'created' | 'updated' | 'skipped' | 'unreachable';

export type WrapperCSSProps = {
  borderColor?: string | null;
  solidBorder?: boolean;
  flashBackgroundColor?: SemanticColor;
  unusedHistoryStep?: boolean;
  focusedHistoryStep?: boolean;
  stepLabelType?: StepLabel;
  stepLabelText?: string;
  dragging?: boolean;
  stepSlotHeight?: StepSlotHeight;
};

const getWrapperCSS = ({
  borderColor,
  solidBorder,
  flashBackgroundColor,
  unusedHistoryStep,
  focusedHistoryStep,
  stepSlotHeight = STEP_SLOT_HEIGHT.edit,
}: WrapperCSSProps) => css`
  width: ${STEP_CARD_WIDTH}px;
  max-height: ${stepSlotHeight};

  ${getFlashAnimation(flashBackgroundColor)}
  width: ${STEP_CARD_WIDTH}px;
  ${shadowLight};
  border-radius: ${CARD_BORDER_RADIUS_OLD};
  background: var(--color-background-white);
  ${focusedHistoryStep
    ? css`
        box-shadow: 0 18px 26px
          color-mix(in srgb, var(--color-background-black) 30%, transparent);
      `
    : shadowLight}
  ${unusedHistoryStep
    ? css`
        opacity: 0.5;
      `
    : ''}
  ${borderColor
    ? css`
        border: solid ${solidBorder ? 2 : 1}px ${borderColor};
      `
    : ''}
`;

export const Wrapper = styled.div<WrapperCSSProps>`
  ${getWrapperCSS}
`;

export const AnchorAsWrapper = styled.a<WrapperCSSProps>`
  ${getWrapperCSS}
  display: block;
  cursor: pointer;
`;

export const ColorBar = styled.div`
  height: 2px;
  background: ${({ color }) => color};
`;

export const Header = styled.div`
  padding: 7px 10px;
  display: flex;
  align-items: center;
  min-height: 31px; // 17px for the 3-dot menu + 14px for the padding
  h4 {
    flex: 1;
    line-height: 1.2; // Wrestling against styles from the date picker, only appears in build
  }
`;

const getShadow = (
  dragging?: boolean,
  focusedHistoryStep?: boolean,
  hasBorder?: boolean
) => {
  if (dragging) {
    return '0 18px 26px color-mix(in srgb, var(--color-background-black) 6%, transparent)';
  }
  if (focusedHistoryStep) {
    return '0 18px 26px color-mix(in srgb, var(--color-background-black) 30%, transparent)';
  }
  if (hasBorder) {
    return '0 1px 3px 2px color-mix(in srgb, var(--color-background-black) 10%, transparent)';
  }
  return '0 1px 3px color-mix(in srgb, var(--color-background-black) 10%, transparent)';
};

const getLabelTypeShadow = (stepLabelType?: StepLabel) => {
  switch (stepLabelType) {
    case 'created':
      return '0 4px 24px color-mix(in srgb, var(--color-agentic-workflow-action-primary) 30%, transparent)';
    case 'updated':
      return '0 4px 24px color-mix(in srgb, var(--color-feedback-info) 30%, transparent)';
    default:
      return '';
  }
};

const getLabelTypeColor = (stepLabelType?: StepLabel) => {
  switch (stepLabelType) {
    case 'created':
      return 'var(--color-agentic-workflow-action-primary)';
    case 'updated':
      return 'var(--color-agentic-workflow-trigger-primary)';
    default:
      return 'var(--color-background-dark)';
  }
};

export const StyledCard = styled.div<WrapperCSSProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: ${STEP_CARD_WIDTH}px;
  padding: ${STEP_CARD_PADDING}px;
  border-radius: ${CARD_BORDER_RADIUS};
  background: var(--color-background-white);

  ${({
    borderColor,
    flashBackgroundColor,
    unusedHistoryStep,
    focusedHistoryStep,
    stepLabelType,
    stepLabelText,
    dragging,
    stepSlotHeight = STEP_SLOT_HEIGHT.history,
  }) => css`
    max-height: ${stepSlotHeight}px;
    ${getFlashAnimation(flashBackgroundColor)}
    border: solid ${CARD_BORDER_WIDTH}px ${borderColor || 'transparent'};
    box-shadow: ${getShadow(
      dragging,
      focusedHistoryStep,
      Boolean(borderColor)
    )};
    ${unusedHistoryStep
      ? css`
          background-color: var(--color-background-lightest);
          & > * {
            opacity: 0.5;
          }
        `
      : ''}
    ${stepLabelType
      ? css`
          box-shadow: ${[
            getShadow(dragging, focusedHistoryStep, Boolean(borderColor)),
            getLabelTypeShadow(stepLabelType),
          ]
            .filter(Boolean)
            .join(', ')};
          position: relative;
          &::after {
            content: '${stepLabelText}';
            position: absolute;
            transform: translateY(-100%);
            top: -${CARD_BORDER_WIDTH}px;
            left: ${CARD_BORDER_RADIUS};
            height: 16px;
            width: fit-content;
            border-top-left-radius: ${borderRadii.small};
            border-top-right-radius: ${borderRadii.small};
            background: ${getLabelTypeColor(stepLabelType)};
            color: var(--color-font-white);
            font-size: 10px;
            font-weight: 700;
            line-height: 10px;
            display: flex;
            padding: 4px 8px 3px;
          }
        `
      : ''}
  `}
`;

export const StyledCardHeader = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledCardSubheader = styled.div`
  min-width: 0;
  & > span {
    display: flex;
  }
`;

export const StyledBody = styled.div`
  max-height: calc(2 * ${fontSizes.text}); // 2 lines, fallback from line clamp
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: break-word;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: -webkit-box;
`;

export const Body = styled(StyledBody)`
  padding: 0 10px;
  margin-bottom: 7px;
`;

export const UserDescription = styled(Body)`
  span {
    font-style: italic;
    color: var(--color-font-secondary);
  }
`;

const USER_DESCRIPTION_UNDERLINE_GAP = 3;

export const StyledDashedUnderline = styled.div<{
  hovered?: boolean;
}>`
  ${({ hovered }) =>
    hovered
      ? () => underlineDottedStyling({ error: false })
      : css`
          &:not(:hover) {
            background-image: none;
          }
        `}
  padding-bottom: ${USER_DESCRIPTION_UNDERLINE_GAP}px;
  margin-bottom: -${USER_DESCRIPTION_UNDERLINE_GAP}px;
`;

export const StyledUserDescription = styled(StyledBody)`
  span {
    font-style: italic;
    color: var(--color-font-secondary);
  }
`;

export const StyledMenu = styled.div`
  position: absolute;
  ${shadowLight}
`;

export const StyledMenuSelect = styled(BaseSelect)`
  && {
    width: auto;
  }
`;

export const ThreeDotsMenu = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 5px;
`;

export const RerunContainer = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 5px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const StyledClickableLink = styled(TextEllipsisWithTooltip)`
  color: inherit;
  &:focus:not(:hover) {
    color: inherit;
    text-decoration: none;
  }
`;

export const StyledCardContent = styled.div<{ hasBorder: boolean }>`
  ${({ hasBorder }) => css`
    border-radius: calc(${CARD_BORDER_RADIUS_OLD} - ${hasBorder ? 2 : 0}px);
  `}
  overflow: hidden;
`;

export const StyledPluginApp = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 18px;
  & > img {
    max-height: 18px;
    max-width: 60px;
    object-fit: contain;
    object-position: left;
    flex-shrink: 0;
  }
  & > span {
    min-width: 0;
  }
`;
